import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type AiSuggestionView, type BulkEditOutput, type CommandDraftView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { GlassIconButton } from '../../../components/ui/glass-icon-button';
import { errorMessage } from '../../../lib/error-message';
import { aiApi } from '../../ai/api';
import { useTrackedAiRun } from '../../ai/use-ai-run';
import { useAiRunConfirm } from '../../ai/components/ai-run-confirm';

/**
 * Sửa hàng loạt bằng lời, có xem trước — APM-AI-m (`plan-ai-features.md` mục 6).
 *
 * ## Vì sao bản xem trước là phần quan trọng nhất
 *
 * Một câu như "dời mọi việc của Minh sang Hà" nghe rõ ràng với người nói và mơ
 * hồ với mọi người khác: việc đang review có tính không, việc đã xong thì sao,
 * "tuần sau" là tuần nào. Model đoán, và đoán sai là chuyện bình thường.
 *
 * Nên màn này không hỏi "có chắc không" — nó hiện **từng dòng** và để người dùng
 * bỏ đi cái nào sai. Bấm duyệt là duyệt đúng những dòng còn lại, và chúng chạy
 * trong một transaction: không có trạng thái nửa chừng để dọn.
 */
export function BulkEditDialog({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const track = useTrackedAiRun();
  const aiConfirm = useAiRunConfirm(projectId);
  const queryClient = useQueryClient();

  const [prompt, setPrompt] = useState('');
  const [draft, setDraft] = useState<{ id: string; output: BulkEditOutput } | null>(null);
  /**
   * Hộp thoại có đang thu nhỏ không — APM-AI-m2.
   *
   * Lượt chạy mất vài chục giây và **không có gì để làm trong hộp thoại** suốt
   * thời gian đó, trong khi bảng theo dõi ở cạnh phải đang kể từng bước: đọc
   * task nào, gọi công cụ gì. Lớp phủ che đúng thứ đáng xem nhất, nên nó tự thu
   * lại khi bấm Xem trước.
   */
  const [minimised, setMinimised] = useState(false);
  /** Chỉ số người dùng đã bỏ — gửi nguyên lên server, server tự dựng lại lệnh. */
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());

  const propose = useMutation({
    mutationFn: async () => {
      // Thu hộp thoại lại **trước** khi gọi: người dùng cần thấy bảng bước chạy
      // ngay từ bước đầu, không phải sau khi mọi thứ đã xong.
      setMinimised(true);
      const suggestion = await track(
        {
          skill: 'bulk_edit',
          label: t('bulkEdit.running'),
          /*
            Chỉ `describe`, **không** `apply` — APM-AI-m2.

            Đã thử đặt `apply` làm nút "Xem kết quả" ở bảng theo dõi. Sai hợp
            đồng: `applyOf` gọi `markApplied()` ngay sau đó, nên bảng chuyển
            sang "Đã áp dụng ✓" trong khi **chưa có gì được ghi**. Người dùng
            đọc đúng như nó nói và tưởng thay đổi đã vào dự án — một lời nói dối
            tệ hơn hẳn việc thiếu một nút.

            Chính chú thích của `TrackOptions.apply` đã cảnh báo điều này: đừng
            đặt một hành động rỗng cho đủ bộ. Khe ấy dành cho việc **ghi thật**.

            Đường quay lại bản xem trước là dải ở đáy màn hình, luôn hiện suốt
            lượt chạy — xem nhánh `minimised` bên dưới.
          */
          describe: (result: never) =>
            t('bulkEdit.proposed', {
              count: ((result as AiSuggestionView).content as BulkEditOutput).items.length,
            }),
        },
        (runId) => aiApi.runSkill('bulk_edit', { projectId, prompt, runId }),
      );
      return suggestion;
    },
    onSuccess: (suggestion) => {
      const output = suggestion.content as BulkEditOutput;
      // `draftId` do model chép từ kết quả `propose_commands`; grounding check
      // đã xác nhận nó có thật, nên client tin được để gọi bước duyệt.
      setDraft({ id: output.draftId, output });
      setExcluded(new Set());
    },
    onError: (error) => {
      toast.error(errorMessage(error, t));
      // Hỏng thì mở lại: lý do đầy đủ nằm ở bảng theo dõi, nhưng người dùng
      // cần ô nhập để sửa câu lệnh rồi thử lại.
      setMinimised(false);
    },
  });

  const apply = useMutation({
    mutationFn: (): Promise<CommandDraftView> => aiApi.applyCommandDraft(draft!.id, [...excluded]),
    onSuccess: async (view) => {
      toast.success(t('bulkEdit.applied', { count: view.appliedCount ?? 0 }));
      /*
        Nạp lại **mọi** truy vấn — APM-AI-m2.

        Bản cũ gọi `invalidateQueries({ queryKey: ['projects', projectId] })`,
        và nó khớp **không gì cả**: `invalidateQueries` so theo *tiền tố*, còn
        board là `['board', projectId, …]`, backlog là `['backlog', …]`, và
        danh sách dự án là `['projects']` — ngắn hơn tiền tố nên cũng trượt.
        Không có lỗi nào được ném ra; board chỉ đơn giản giữ dữ liệu cũ cho tới
        khi người dùng F5.

        Không liệt kê từng khoá vì một lượt sửa hàng loạt đổi được người phụ
        trách, trạng thái, mức ưu tiên và ngày — tức chạm board, backlog, danh
        sách, Gantt, tải nhân sự, báo cáo và "việc của tôi". Một danh sách khoá
        viết tay ở đây sẽ thiếu đúng cái màn hình mà người dùng đang mở. Dải
        lệch tiến độ (`DriftBanner`) đã chọn cách này, cùng lý do.
      */
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const items = draft?.output.items ?? [];
  const remaining = items.length - excluded.size;

  /*
    Thu nhỏ thành một dải ở đáy, **không** đóng hẳn — APM-AI-m2.

    Đóng hẳn thì bản nháp lệnh vừa dựng mất theo state, và người dùng phải chạy
    lại một lượt AI cho thứ vừa có. Dải này luôn có đường quay lại, kể cả khi
    bảng theo dõi đã bị tắt — không để người dùng vào ngõ cụt.
  */
  if (minimised) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3">
        <div className="flex items-center gap-3 rounded-full border border-border bg-background px-4 py-2 shadow-lg">
          <span className="text-sm">
            {propose.isPending
              ? t('bulkEdit.running')
              : draft
                ? t('bulkEdit.proposed', { count: items.length })
                : t('bulkEdit.title')}
          </span>
          <Button type="button" size="sm" onClick={() => setMinimised(false)}>
            {draft ? t('bulkEdit.openResult') : t('bulkEdit.restore')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center popup-scrim p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col gap-3 overflow-auto rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-2xl on-scrim">
        <header className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('bulkEdit.title')}</h2>
          <div className="flex items-center gap-2">
            {/* Thu nhỏ bằng tay: bản xem trước dài có thể cần đối chiếu với
                board phía sau, không chỉ lúc đang chạy. */}
            <Button type="button" variant="ghost" size="sm" onClick={() => setMinimised(true)}>
              {t('bulkEdit.minimise')}
            </Button>
            {/* Sheet iOS 26 như `Dialog`: ✓ là duyệt bản đề xuất, ✕ ở mép phải
                là đóng. ✓ chỉ có ở bước xem kết quả — bước nhập lời thì nút
                chính là "Xem trước", một lượt chạy AI tính tiền, nên giữ nút chữ. */}
            {draft ? (
              <GlassIconButton
                variant="apply"
                label={apply.isPending ? t('bulkEdit.applying') : t('bulkEdit.apply')}
                pending={apply.isPending}
                disabled={apply.isPending || remaining === 0}
                onClick={() => apply.mutate()}
              />
            ) : null}
            <GlassIconButton variant="close" onClick={onClose} label={t('common.close')} />
          </div>
        </header>

        {draft === null ? (
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              aiConfirm.confirm('bulk_edit', t('bulkEdit.preview'), () => propose.mutate());
            }}
          >
            <label className="flex flex-col gap-1 text-xs">
              {t('bulkEdit.prompt')}
              <textarea
                value={prompt}
                autoFocus
                rows={3}
                maxLength={2000}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={t('bulkEdit.placeholder')}
                className="rounded-DEFAULT border border-input bg-background p-2 text-sm"
              />
            </label>
            <p className="text-xs text-muted-foreground">{t('bulkEdit.help')}</p>
            <div className="flex justify-end">
              <Button type="submit" disabled={propose.isPending || prompt.trim().length < 5}>
                {propose.isPending ? t('bulkEdit.running') : t('bulkEdit.preview')}
              </Button>
            </div>
            {aiConfirm.dialog}
          </form>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm">{draft.output.summary}</p>
            <ul className="flex flex-col gap-1">
              {items.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="ios-check ios-check-select"
                    checked={!excluded.has(index)}
                    aria-label={item.humanLine}
                    onChange={(event) => {
                      const next = new Set(excluded);
                      if (event.target.checked) next.delete(index);
                      else next.add(index);
                      setExcluded(next);
                    }}
                  />
                  <span className="flex flex-col">
                    <span>{item.humanLine}</span>
                    {item.warning ? (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        {item.warning}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
            <span className="text-xs text-muted-foreground">
              {t('bulkEdit.remaining', { count: remaining })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
